import currency from "currency.js";
import { TEXT_COLORS } from "../../../constants/design/colors";
import { SPACING } from "../../../constants/design/spacing";
import {
  CONVERT_UI_ENABLED,
  TRANSFER_UI_ENABLED,
} from "../../../constants/featureFlags";
import { ITableColumn } from "../../../interfaces/Table/TableColumn";
import { UNKNOWN_DISPLAY } from "../../../utils/unknownNumbers";
import {
  getAssetLogo,
  getAssetShortName,
  getCollateralPrecision,
} from "../../../utils/asset/assets";
import FillMeter from "../../FillMeter";
import RowActionButton from "../../shared/RowActionButton";
import { Align, ButtonGroup, GroupedCells } from "../../shared/Table/style";
import { AllCollaterals } from "../../../constants/addresses";
import { AssetCell } from "./style";
import { ICollateralRow } from "./Token.types";

export function getCollateralColumns({
  t,
  onDeposit,
  onWithdraw,
  onTransfer,
  onConvert,
}: {
  t: any;
  onDeposit?: (asset: AllCollaterals) => void;
  onWithdraw?: (asset: AllCollaterals) => void;
  onTransfer?: (asset: AllCollaterals) => void;
  onConvert?: (asset: AllCollaterals) => void;
}): ITableColumn<ICollateralRow>[] {
  return [
    {
      title: t("asset"),
      accessor: "asset",
      align: "left",
      Cell: ({ value }) => (
        <Align align="left">
          <AssetCell>
            <img src={getAssetLogo(value)} alt={value} />
            <span>{getAssetShortName(value)}</span>
          </AssetCell>
        </Align>
      ),
    },
    {
      title: t("balance"),
      accessor: "balance",
      align: "right",
      Cell: ({ value, row }) => (
        <Align align="right">
          {value === undefined
            ? UNKNOWN_DISPLAY
            : currency(value, {
                precision: getCollateralPrecision(row.original.asset),
              }).format({ symbol: "" })}
        </Align>
      ),
    },
    {
      title: t("composition"),
      accessor: "composition",
      align: "right",
      Cell: ({ value }) => (
        <Align align="right">
          {/* String(undefined) is the literal text "undefined"; an unknown
              composition has no bar to draw. */}
          {value === undefined ? (
            UNKNOWN_DISPLAY
          ) : (
          <FillMeter
            layoutReversed
            decimals={2}
            fill={String(value)}
            textColor={TEXT_COLORS.two}
            barStyle={{
              height: 4,
              marginLeft: SPACING.three,
            }}
          />
          )}
        </Align>
      ),
    },
    {
      title: t("value"),
      accessor: "value",
      align: "right",
      Cell: ({ value }) => (
        <Align align="right" style={{ color: TEXT_COLORS.one }}>
          {value === undefined ? UNKNOWN_DISPLAY : currency(value).format()}
        </Align>
      ),
    },
    {
      title: t("actions"),
      accessor: "id",
      align: "right",
      Cell: ({ value, row }) => {
        // An UNKNOWN balance is not a positive one: unknown means "no action
        // available", not zero. Same rule as pages/AssetsTab.
        const hasBalance = (row.original.balance ?? 0) > 0;
        return (
          <Align align="right">
            <GroupedCells>
              <ButtonGroup>
                {onDeposit && (
                  <RowActionButton
                    variant={"text"}
                    text={t("deposit")}
                    onClick={() => onDeposit?.(value)}
                    style={{ marginRight: `${SPACING.two}px` }}
                  />
                )}
                <RowActionButton
                  disabled={!hasBalance || !onWithdraw}
                  variant={"text"}
                  text={t("withdraw")}
                  onClick={() => onWithdraw?.(value)}
                  style={{ marginRight: `${SPACING.two}px` }}
                />
                {/* Transfer disabled from UI/UX — flip TRANSFER_UI_ENABLED to re-enable */}
                {TRANSFER_UI_ENABLED && (
                  <RowActionButton
                    disabled={!hasBalance || !onTransfer}
                    variant={"text"}
                    text={t("transfer")}
                    onClick={() => onTransfer?.(value)}
                    style={{ marginRight: `${SPACING.two}px` }}
                  />
                )}
                {/* Convert disabled from UI/UX — flip CONVERT_UI_ENABLED to re-enable */}
                {CONVERT_UI_ENABLED && (
                  <RowActionButton
                    disabled={!hasBalance || !onConvert}
                    variant={"text"}
                    text={t("convert")}
                    onClick={() => onConvert?.(value)}
                  />
                )}
              </ButtonGroup>
            </GroupedCells>
          </Align>
        );
      },
    },
  ];
}
